import { Lightning } from '@lightningjs/sdk';
import { FULL_HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { Button } from '../../../button/button.component';
import { getLayoutTheme } from '../../../utils/theme';
import {
  DETACHED_SIGNAL,
  FOCUSED_SIGNAL,
  IMPRESSION_TRACK_SIGNAL,
  INACTIVE_SIGNAL,
  PORTION_REQUESTED_SIGNAL,
  SELECTED_SIGNAL,
  SHOW_GRID_SIGNAL,
} from '../../../signals';
import { t } from '../../../../i18n';
import { backToBuffer, cleanupBuffer, getFromBuffer, PAGE_STATE, PLACEHOLDER } from '../../../utils/virtualization';
import { cancelPlacement, schedulePlacement } from '../../../utils/placement';
import { isImpressionTrackingEnabled } from '../../../utils/impression';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../../../utils/sizes';
import { getTitle } from '../utils/block';
import {
  getBlockStyle,
  CONTENT_GAP,
  getInitialStyle,
  defaultContext,
  colors,
  BUTTON_HEIGHT,
  PADDING,
} from './grid.style';
import { templates, ITEMS_PER_ROW, MAX_PREVIEW_ITEMS } from './grid.constants';

export default class Grid extends Lightning.Component {
  static _template() {
    return {
      Content: {
        Title: {},
        Container: {},
      },
      More: {
        type: Button,
        label: t('global.showMore'),
        x: v => v / 2,
      },
    };
  }

  static getOffsetsByBlockData({
    data,
    blockIndex,
    screenSize = FULL_HD,
    showAllItems,
    displayShowMore,
    isSearchPage,
  }) {
    const { title, content } = data;
    const hasTitle = Boolean(getTitle(title));

    const {
      items,
      padding: { bottom },
    } = getBlockStyle({
      hasTitle,
      isFirstBlock: blockIndex === 0,
      screenSize,
      isSearchPage,
    });

    return {
      top: items.y,
      bottom:
        bottom +
        (!showAllItems && displayShowMore && content.items.length > MAX_PREVIEW_ITEMS
          ? BUTTON_HEIGHT[screenSize] + PADDING.BOTTOM[screenSize]
          : 0),
    };
  }

  static getRowCount({ content, showAllItems, displayShowMore, isSearchPage }, screenSize) {
    const { contentTemplateId, items, pagination } = content;
    const slicedCount = displayShowMore ? Math.min(MAX_PREVIEW_ITEMS, items.length) : items.length;
    const count = showAllItems ? pagination.totalItems : slicedCount;
    const renderingContext = isSearchPage ? RENDERING_CONTEXT.SEARCH : RENDERING_CONTEXT.GENERAL;

    return Math.ceil(count / ITEMS_PER_ROW[contentTemplateId][renderingContext][screenSize]);
  }

  static getContentGap(screenSize) {
    return CONTENT_GAP[screenSize];
  }

  _construct() {
    this._style = defaultContext;
    this._viewport = {
      w: SCREEN_WIDTH[this._style.screenSize],
      h: SCREEN_HEIGHT[this._style.screenSize],
    };
    this._contentGap = CONTENT_GAP[this._style.screenSize];
    this._isGridActive = false;
    this._isGridAttached = false;
    this._resetVirtualization();
  }

  _active() {
    this._isGridActive = true;

    if (!this._data) {
      return;
    }

    if (this._isDataApplied) {
      this._placeItems();

      return;
    }

    // The first data application is deferred to the placement scheduler, so a grid the user scrolls
    // past never gets to spend a whole frame on items nobody is going to look at
    schedulePlacement(this);
  }

  _build() {
    this.patch(getInitialStyle(this._style));
  }

  _attach() {
    this._isGridAttached = true;
  }

  _inactive() {
    this._isGridActive = false;
    cancelPlacement(this);
    // The queue is rebuilt from scratch on the way back in, so leaving a partially drained one behind would
    // strand the unplaced items: stepPlacement bails while inactive and nothing would pick them up again
    this._pending.length = 0;
    this._pendingCursor = 0;
  }

  _detach() {
    this._isGridAttached = false;
    cancelPlacement(this);
    this._resetVirtualization();
  }

  /**
   * Performs a single unit of deferred work, driven by the placement scheduler
   *
   * @returns {boolean} whether there is more work left
   */
  stepPlacement() {
    if (!this._isGridActive || !this._data) {
      return false;
    }

    if (!this._isDataApplied) {
      // Applying the data queues the placement of the visible window
      this.data = this._data;

      return this._hasPendingItems();
    }

    if (this._hasPendingItems()) {
      this._placeItem(this._pending[this._pendingCursor]);
      this._pendingCursor += 1;
    }

    return this._hasPendingItems();
  }

  get contentGap() {
    return this._contentGap;
  }

  set contentGap(value) {
    this._contentGap = value;
    this._placeItems(true);
  }

  get contentPadding() {
    return this.tag('Content').x;
  }

  set contentPadding(value) {
    this.tag('Content').x = value;
  }

  get itemsPerRow() {
    if (this._itemsPerRow) {
      return this._itemsPerRow;
    }

    const { itemsPerRow, itemTemplate, isSearchPage } = this._data;
    const { screenSize } = this._style;
    const renderingContext = isSearchPage ? RENDERING_CONTEXT.SEARCH : RENDERING_CONTEXT.GENERAL;
    this._itemsPerRow = itemsPerRow || ITEMS_PER_ROW[itemTemplate][renderingContext][screenSize];

    return this._itemsPerRow;
  }

  get data() {
    return this._data;
  }

  set data(value) {
    if (!value) {
      return;
    }

    this._data = value;

    if (!this._isGridActive) {
      return;
    }

    const isUpdating = this._isDataApplied;
    this._isDataApplied = true;
    this._blockIndex = value.blockIndex;
    const { block, blockIndex, theme, rowSize, showAllItems, displayShowMore } = value;
    const { items, pagination } = block.content;
    const blockTheme = getLayoutTheme(block.theme, theme);
    const { totalItems, itemsPerPage } = pagination;
    const { colorScheme } = blockTheme;

    this._id = block.id;
    this._style = { ...this._style, colorMode: blockTheme.colorScheme };

    // NOTE: the item wrappers are built by _getItemData on demand, a paginated grid holds hundreds of items
    // but only ever displays a couple of screens worth of them
    this._sourceItems = showAllItems || !displayShowMore ? items : items.slice(0, MAX_PREVIEW_ITEMS);
    if (this._items.length > this._sourceItems.length) {
      this._items.length = this._sourceItems.length;
    }

    this._pages = block.pages;
    // Pagination is disable when a Grid id displayed within a regular layout
    this._totalItems = showAllItems ? totalItems : this._sourceItems.length;
    this._itemsPerPage = showAllItems ? itemsPerPage : 0;

    const titleText = getTitle(block.title);
    const hasTitle = Boolean(titleText);
    const blockStyle = getBlockStyle({
      hasTitle,
      isFirstBlock: blockIndex === 0,
      screenSize: this._style.screenSize,
      isSearchPage: this._data.isSearchPage,
    });
    const displayShowMoreButton = displayShowMore && !showAllItems && items.length > MAX_PREVIEW_ITEMS;

    this.patch({
      Content: {
        Title: {
          visible: hasTitle,
          color: colors.title[colorScheme],
          text: { text: titleText },
          ...blockStyle.title,
        },
        Container: {
          ...blockStyle.items,
        },
      },
      More: {
        visible: displayShowMoreButton,
        y: rowSize.h - PADDING.BOTTOM[this._style.screenSize],
      },
    });

    this._setIndex(
      Math.min(this._focusedItemIndex, Math.max(this._totalItems - (displayShowMoreButton ? 0 : 1), 0)),
      true,
    );

    // Remove extra items in case of a row data update
    if (isUpdating) {
      const views = Array.from(this._views.keys());
      if (views.length > this._totalItems) {
        for (let i = 0; i < views.length; i += 1) {
          const viewIndex = views[i];
          if (viewIndex >= this._totalItems) {
            this._backToBuffer(this._views.get(viewIndex));
          }
        }
      }
    }

    this._refocus();
  }

  _makeItemData(content, index) {
    const { itemSize, itemTemplate, isSearchPage } = this._data;

    return {
      content,
      index,
      config: itemSize,
      template: itemTemplate,
      colorScheme: this._colorScheme,
      isSearchPage,
    };
  }

  /**
   * Builds, or refreshes in place, the item wrapper of a single index
   *
   * @param {Number} index zero-based item index
   * @returns {Object} the item wrapper, carrying a PLACEHOLDER while the content is not loaded yet
   */
  _getItemData(index) {
    const content = this._sourceItems[index] || PLACEHOLDER;
    const existingItem = this._items[index];

    if (!existingItem) {
      this._items[index] = this._makeItemData(content, index);

      return this._items[index];
    }

    // NOTE: It's faster than creation of a new object
    existingItem.content = content;
    existingItem.config = this._data.itemSize;
    existingItem.template = this._data.itemTemplate;
    existingItem.colorScheme = this._colorScheme;
    existingItem.isSearchPage = this._data.isSearchPage;

    return existingItem;
  }

  get focusedIndex() {
    return this._focusedItemIndex;
  }

  set focusedIndex(value) {
    if (this._totalItems) {
      this._focusedItemIndex = Math.min(this.maxIndex, Math.max(value, 0));
    } else {
      this._focusedItemIndex = value;
    }
  }

  get maxIndex() {
    if (!Number.isFinite(this._totalItems)) {
      return 0;
    }

    return this._totalItems - (this.tag('More').visible ? 0 : 1);
  }

  goTo(index, shouldRefresh = false) {
    this._setIndex(index, shouldRefresh);
  }

  _setIndex(index, shouldRefresh = false) {
    this._focusedItemIndex = index;

    if (!this._totalItems) {
      return;
    }

    if (this._isGridActive) {
      this._placeItems(shouldRefresh);
    }

    this.signal(FOCUSED_SIGNAL, index);

    if (this._itemsPerPage > 0) {
      const rowHeight = this._data.itemSize.h + this._contentGap;
      const visibleRows = Math.ceil(this._viewport.h / rowHeight) + 1;
      const bufferSize = visibleRows * this.itemsPerRow;
      const nextPage = Math.floor(
        Math.min(this._focusedItemIndex + bufferSize, this._totalItems - 1) / this._itemsPerPage,
      );

      // NOTE: use only strict equality here to ensure redundant page requests will not be created
      for (let i = nextPage; i > 0; i -= 1) {
        const isLoaded = this._pages[i] !== PAGE_STATE.MISSING;
        if (isLoaded) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const start = i * this._itemsPerPage;

        this.signal(PORTION_REQUESTED_SIGNAL, {
          id: this._id,
          // NOTE: pages are 1-based
          page: i + 1,
          start,
        });
      }
    }
  }

  _getFromBufferOrCreate(contentId) {
    const { itemType, itemSize, itemTemplate } = this._data;

    const existingView = getFromBuffer(itemTemplate, contentId);
    if (existingView) {
      return existingView;
    }

    return this.stage.c({
      type: itemType,
      w: itemSize.w,
      h: itemSize.h,
      template: itemTemplate,
      isSearchPage: this._data.isSearchPage,
      passSignals: {
        [SELECTED_SIGNAL]: true,
      },
      signals: {
        [INACTIVE_SIGNAL]: '_onViewBecomeInactive',
        [DETACHED_SIGNAL]: '_onViewBecomeDetached',
      },
    });
  }

  _onViewBecomeInactive(view) {
    if (this._isGridAttached) {
      this._backToBuffer(view);
    }
  }

  _onViewBecomeDetached(view) {
    cleanupBuffer(view);
  }

  _backToBuffer(view) {
    // NOTE: _placeItem registers a view under the index it just assigned to it, so there is no need to scan
    if (this._views.get(view.index) === view) {
      this._views.delete(view.index);
      this._staleIndices.delete(view.index);
    }

    backToBuffer(view);
    view.visible = false;
  }

  _placeItems(shouldRefresh = false) {
    if (!this._data || !this._totalItems) {
      return;
    }

    const rowHeight = this._data.itemSize.h + this._contentGap;
    // NOTE: the layout keeps the focused row roughly centred, so half a viewport of rows on each side covers
    // what can be on screen. `visibleRows` already carries a spare row, which doubles as the scroll margin
    const visibleRows = Math.ceil(this._viewport.h / rowHeight) + 1;
    const rowsPerSide = Math.ceil(visibleRows / 2);
    const focusedRow = Math.floor(this._focusedItemIndex / this.itemsPerRow);
    const startIndex = Math.max((focusedRow - rowsPerSide) * this.itemsPerRow, 0);
    const endIndex = Math.min((focusedRow + rowsPerSide + 1) * this.itemsPerRow - 1, this._totalItems - 1);

    if (shouldRefresh) {
      // The content behind the placed views has changed, so each has to take it again when it is next reached
      this._views.forEach((view, index) => this._staleIndices.add(index));
    }

    this._trackImpressions(startIndex, endIndex);

    // The focused view must exist synchronously, _getFocused() relies on it
    if (!this._views.has(this._focusedItemIndex) || this._staleIndices.has(this._focusedItemIndex)) {
      this._placeItem(this._focusedItemIndex);
    }

    // The rest is queued from the focus outwards, so the items closest to the eye are placed first
    this._pending.length = 0;
    this._pendingCursor = 0;

    const maxDistance = Math.max(this._focusedItemIndex - startIndex, endIndex - this._focusedItemIndex);
    for (let distance = 1; distance <= maxDistance; distance += 1) {
      const next = this._focusedItemIndex + distance;
      const previous = this._focusedItemIndex - distance;

      // An item keeps its position whatever the focus does, so one already in place is left alone unless stale
      if (next <= endIndex && (!this._views.has(next) || this._staleIndices.has(next))) {
        this._pending.push(next);
      }

      if (previous >= startIndex && (!this._views.has(previous) || this._staleIndices.has(previous))) {
        this._pending.push(previous);
      }
    }

    if (this._hasPendingItems()) {
      schedulePlacement(this);
    }
  }

  _hasPendingItems() {
    return this._pendingCursor < this._pending.length;
  }

  _placeItem(index) {
    const itemData = this._getItemData(index);
    const container = this.tag('Container');

    let view = this._views.get(index);
    if (!view) {
      view = this._getFromBufferOrCreate(itemData.content.id);
    }

    // NOTE: re-parenting has to happen before the data is applied, since it detaches the view and a detached
    // content template drops the item it is displaying
    if (view.parent !== container) {
      container.childList.add(view);
    }

    view.index = index;
    view.item = itemData;
    view.x = (index % this.itemsPerRow) * (this._data.itemSize.w + this._contentGap);
    view.y = Math.floor(index / this.itemsPerRow) * (this._data.itemSize.h + this._contentGap);
    view.visible = true;
    this._views.set(index, view);
    this._staleIndices.delete(index);
  }

  _trackImpressions(startIndex, endIndex) {
    if (!isImpressionTrackingEnabled()) {
      return;
    }

    const tracked = [];

    for (let index = startIndex; index <= endIndex; index += 1) {
      if (this._sourceItems[index]) {
        tracked.push(index);
      }
    }

    if (tracked.length) {
      this.signal(IMPRESSION_TRACK_SIGNAL, this._blockIndex, tracked);
    }
  }

  _handleUp() {
    if (this._focusedItemIndex === this._totalItems) {
      this._setIndex(Number.isFinite(this._lastGridIndex) ? this._lastGridIndex : this._totalItems - 1);
      this._lastGridIndex = undefined;

      return true;
    }

    if (this._focusedItemIndex >= this.itemsPerRow) {
      this._setIndex(this._focusedItemIndex - this.itemsPerRow);

      return false;
    }

    return false;
  }

  _handleDown() {
    const index = this._focusedItemIndex + this.itemsPerRow;

    if (index <= this._totalItems - 1) {
      this._setIndex(index);
    } else {
      const rowCount = Math.ceil(this._totalItems / this.itemsPerRow);
      const currentRow = Math.floor(this._focusedItemIndex / this.itemsPerRow);

      if (currentRow < rowCount - 1) {
        this._setIndex(this._totalItems - 1);
      } else if (this._focusedItemIndex < this._totalItems && this.tag('More').visible) {
        this._lastGridIndex = this._focusedItemIndex;
        this._focusedItemIndex = this._totalItems;

        return true;
      }
    }

    return false;
  }

  _handleLeft() {
    if (this._focusedItemIndex % this.itemsPerRow === 0) {
      return false;
    }
    if (this._focusedItemIndex > 0) {
      this._setIndex(this._focusedItemIndex - 1);

      return true;
    }

    return false;
  }

  _handleRight() {
    if (this._focusedItemIndex % this.itemsPerRow === this.itemsPerRow - 1) {
      return false;
    }

    if (this._focusedItemIndex < this._totalItems - 1) {
      this._setIndex(this._focusedItemIndex + 1);

      return true;
    }

    return false;
  }

  _handleEnter() {
    if (this._focusedItemIndex === this._totalItems) {
      this.signal(SHOW_GRID_SIGNAL, this._data);

      return true;
    }

    this.signal(SELECTED_SIGNAL, this._items[this._focusedItemIndex]);

    return true;
  }

  _focus() {
    // The focused grid has to be usable right away, so it does not wait for its scheduled turn
    if (!this._isDataApplied && this._data) {
      this.data = this._data;

      return;
    }

    this._placeItems();
  }

  _getFocused() {
    if (this._focusedItemIndex >= this._totalItems) {
      return this._tag('More');
    }

    return this._views.get(this._focusedItemIndex);
  }

  _resetVirtualization() {
    this._data = undefined;
    this._isDataApplied = false;
    this._focusedItemIndex = 0;
    this._itemsPerPage = 0;
    // Indicates an item index in the bottom row in case there is a Show More button
    this._lastGridIndex = undefined;
    this._items = [];
    this._sourceItems = [];
    this._views = new Map();
    this._pages = {};
    this._pending = [];
    this._pendingCursor = 0;
    this._staleIndices = new Set();
  }
}

Grid.Templates = templates;
